import { makeUseAxios } from 'axios-hooks';

import { API, defaultApiTransformResponse } from 'src/app/services/axios';
import type { IVendorLeadBillingInfo } from 'src/types/vendors/IVendorLeadBillingInfo';
import type { IVendorLeadBillingInfoUpdate } from 'src/types/vendors/IVendorLeadBillingInfoUpdate';
import type { IVendorLeadContactCreate } from 'src/types/vendors/IVendorLeadContactCreate';
import type { IVendorLeadContactUpdate } from 'src/types/vendors/IVendorLeadContactUpdate';
import type { IVendorLeadProduct } from 'src/types/vendors/IVendorLeadProduct';
import { toVendorLeadProduct } from 'src/types/vendors/IVendorLeadProductResponse';
import type { IVendorLeadProductPartialUpdate } from 'src/types/vendors/IVendorLeadProductUpdate';

const useAxiosApi = makeUseAxios({
  axios: API,
});

const vendorProductLeadTransformResponse = defaultApiTransformResponse.concat(toVendorLeadProduct);

const BASE_PATH = '/leads/vendors';

export const vendorsApi = {
  useGetVendorProductLeadQuery: (productLeadId: string) =>
    useAxiosApi<IVendorLeadProduct>({
      url: `${BASE_PATH}/${productLeadId}`,
      method: 'GET',
      transformResponse: vendorProductLeadTransformResponse,
    }),

  useManualGetVendorProductLeadQuery: (productLeadId: string) =>
    useAxiosApi<IVendorLeadProduct>(
      {
        url: `${BASE_PATH}/${productLeadId}`,
        method: 'GET',
        transformResponse: vendorProductLeadTransformResponse,
      },
      {
        manual: true,
      }
    ),

  usePartialUpdateVendorProductLeadMutation: (vendorLeadProductId: string) =>
    useAxiosApi<IVendorLeadProduct, IVendorLeadProductPartialUpdate>(
      {
        url: `${BASE_PATH}/${vendorLeadProductId}`,
        method: 'PATCH',
        responseType: 'json',
        // Lead product update data (`data`) will be set in refetch function
      },
      {
        manual: true,
      }
    ),

  useCreateVendorLeadContactMutation: (vendorLeadId: string) =>
    useAxiosApi<void, IVendorLeadContactCreate>(
      {
        url: `${BASE_PATH}/${vendorLeadId}/contacts`,
        method: 'POST',
        responseType: 'json',
        // Lead contact create data (`data`) will be set in mutate function
      },
      {
        manual: true,
      }
    ),

  useUpdateVendorLeadContactMutation:
    (vendorLeadId: string) =>
    async ({ id, ...bodyParams }: IVendorLeadContactUpdate) =>
      await API.put<void>(`${BASE_PATH}/${vendorLeadId}/contacts/${id}`, { ...bodyParams }),

  useDeleteVendorLeadContactMutation: (vendorLeadId: string) => async (vendorLeadContactId: string) =>
    await API.delete<void>(`${BASE_PATH}/${vendorLeadId}/contacts/${vendorLeadContactId}`),

  // Billing
  useGetVendorLeadBillingInfoQuery: (vendorLocationId: string) =>
    useAxiosApi<IVendorLeadBillingInfo>({
      url: `${BASE_PATH}/billing/${vendorLocationId}`,
      method: 'GET',
    }),

  useUpdateVendorLeadBillingInfoMutation: (vendorLocationId: string) =>
    useAxiosApi<IVendorLeadBillingInfo, IVendorLeadBillingInfoUpdate>(
      {
        url: `${BASE_PATH}/billing/${vendorLocationId}`,
        method: 'PUT',
        responseType: 'json',
      },
      {
        manual: true,
      }
    ),
};
